import { useState, useCallback, useMemo, useEffect } from 'react';
import type { ContentPack, BuildState } from '../../packs/types';
import type { Adapters } from '../../adapters/types';
import { randomName } from '../wordlist';
import { useFleet } from './useFleet';
import { useShareURL } from './useShareURL';
import { buildPalette, hslToHex } from '../../pixel/palette';

export function useComposerState(pack: ContentPack, adapters: Adapters) {
  const [build, setBuild] = useState<BuildState>(() => ({ ...pack.defaultBuild }));
  const [hue, setHue] = useState(210);
  const [accentHue, setAccentHue] = useState(40);
  const [glowHue, setGlowHue] = useState(180);
  const [detailHue, setDetailHue] = useState(45);
  const [trimHue, setTrimHue] = useState(210);
  const [backgroundHue, setBackgroundHue] = useState(222);
  const [primarySat, setPrimarySat] = useState(100);
  const [accentSat, setAccentSat] = useState(100);
  const [glowSat, setGlowSat] = useState(100);
  const [detailSat, setDetailSat] = useState(100);
  const [trimSat, setTrimSat] = useState(100);
  const [bgSat, setBgSat] = useState(100);
  const [borderEnabled, setBorderEnabled] = useState(false);
  const [borderColor, setBorderColor] = useState('#000000');

  const baseCategoryId = pack.metadata?.baseCategoryId ?? 'hull';

  const background = useMemo(() => hslToHex(backgroundHue, Math.round(35 * bgSat / 100), 5), [backgroundHue, bgSat]);
  const paletteSats = useMemo(() => ({ primary: primarySat, accent: accentSat, glow: glowSat, detail: detailSat, trim: trimSat }), [primarySat, accentSat, glowSat, detailSat, trimSat]);
  const palette = useMemo(() => buildPalette(hue, accentHue, glowHue, detailHue, trimHue, paletteSats), [hue, accentHue, glowHue, detailHue, trimHue, paletteSats]);

  const { fleet, load: loadFleet, save: saveFleet, remove: removeFleet } = useFleet(adapters.storage);

  const selectPart = useCallback((categoryId: string, key: string | null) => {
    setBuild(s => ({ ...s, [categoryId]: key }));
  }, []);

  const randomize = useCallback(() => {
    const rHue = () => Math.floor(Math.random() * 360);
    const baseKeys = Object.keys(pack.hulls);
    const newBuild: BuildState = { [baseCategoryId]: baseKeys[Math.floor(Math.random() * baseKeys.length)] };
    for (const cat of pack.categories) {
      if (cat.id === baseCategoryId) continue;
      newBuild[cat.id] = (cat.optional && Math.random() < 0.4) ? null : cat.keys[Math.floor(Math.random() * cat.keys.length)];
    }
    newBuild.name = randomName();
    setBuild(newBuild);
    setHue(rHue()); setAccentHue(rHue()); setGlowHue(rHue());
    setDetailHue(rHue()); setTrimHue(rHue()); setBackgroundHue(rHue());
    setPrimarySat(100); setAccentSat(100); setGlowSat(100);
    setDetailSat(100); setTrimSat(100); setBgSat(100);
  }, [pack, baseCategoryId]);

  // Sync share URL
  useShareURL(pack, adapters.navigation, { build, hue, accentHue, glowHue, detailHue, trimHue, backgroundHue, primarySat, accentSat, glowSat, detailSat, trimSat, bgSat, borderEnabled, borderColor }, (payload) => {
    if (payload.packId !== pack.id) return;
    setBuild(payload.build);
    setHue(payload.hue); setAccentHue(payload.accentHue); setGlowHue(payload.glowHue);
    setDetailHue(payload.detailHue); setTrimHue(payload.trimHue); setBackgroundHue(payload.backgroundHue);
    setPrimarySat(payload.primarySat); setAccentSat(payload.accentSat); setGlowSat(payload.glowSat);
    setDetailSat(payload.detailSat); setTrimSat(payload.trimSat); setBgSat(payload.bgSat);
    setBorderEnabled(payload.borderEnabled); setBorderColor(payload.borderColor);
  });

  return {
    state: { build, hue, accentHue, glowHue, detailHue, trimHue, backgroundHue, primarySat, accentSat, glowSat, detailSat, trimSat, bgSat, borderEnabled, borderColor, background, palette, baseCategoryId },
    setters: { setBuild, setHue, setAccentHue, setGlowHue, setDetailHue, setTrimHue, setBackgroundHue, setPrimarySat, setAccentSat, setGlowSat, setDetailSat, setTrimSat, setBgSat, setBorderEnabled, setBorderColor },
    actions: { selectPart, randomize, loadFleet, saveFleet, removeFleet },
    fleet
  };
}
